import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "../lib/api";

const PILLAR_NAMES = {
  lean: ["I", "Lean"],
  six_sigma: ["II", "Six Sigma"],
  design_thinking: ["III", "Design Thinking"],
  systems_thinking: ["IV", "Systems Thinking"],
  chaos_engineering: ["V", "Chaos Engineering"],
  agile_scrum: ["VI", "Agile / Scrum"],
};

export default function Seal() {
  const { projectId } = useParams();
  const [seal, setSeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // NOTE: public endpoint — send request without auth headers
        const r = await fetch(`${api.defaults.baseURL}/seal/${projectId}`);
        if (!r.ok) throw new Error(`${r.status}`);
        setSeal(await r.json());
      } catch (e) {
        setErr("Seal not found or withheld.");
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const copyLink = () => {
    // Copy the share URL (with OG tags for social previews) — falls back to direct URL
    const origin = window.location.origin;
    const shareUrl = `${origin}/api/seal/${projectId}/share`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareOnX = () => {
    // Prefilled tweet: rank + name + public seal URL. The API share-URL emits OG meta so
    // X/Twitter fetches the custom hexagram preview card.
    const origin = window.location.origin;
    const shareUrl = `${origin}/api/seal/${projectId}/share`;
    const rankStr = seal?.rank?.name ? `${seal.rank.name} · ` : "";
    const chaosStr = seal?.chaos?.passed ? "Chaos PASSED" : "forging in progress";
    const text = `${rankStr}${seal?.name || "A sprint"} — ${chaosStr}. Forged in the HEXIS Lyceum.`;
    const tweet = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(tweet, "_blank", "noopener,noreferrer,width=600,height=500");
  };

  const downloadFile = (kind) => {
    const origin = window.location.origin;
    const a = document.createElement("a");
    a.href = `${origin}/api/seal/${projectId}/download.${kind}`;
    a.download = "";
    document.body.appendChild(a); a.click(); a.remove();
  };

  if (loading) return <div style={{ padding: "25vh 0", textAlign: "center" }} className="overline">Consulting the Codex...</div>;
  if (err || !seal) return (
    <div style={{ padding: "25vh 0", textAlign: "center" }}>
      <div className="font-display" style={{ fontSize: 60, color: "var(--brass)", fontStyle: "italic" }}>Ø</div>
      <div className="font-serif" style={{ fontSize: 28, marginTop: 10 }}>The seal is blank.</div>
      <div className="font-mono" style={{ color: "var(--muted)", fontSize: 12, marginTop: 8, letterSpacing: "0.2em" }}>{err || "—"}</div>
    </div>
  );

  const pct = Math.round((seal.pillars_completed.length / (seal.pillars_total || 6)) * 100);

  return (
    <div data-testid="seal-page" style={{ padding: "60px 4vw", maxWidth: 900, margin: "0 auto" }}>
      {/* Certificate */}
      <div
        className="glass reveal"
        style={{
          padding: "60px 48px",
          border: "1px solid var(--brass)",
          position: "relative",
          textAlign: "center",
        }}
      >
        {/* Corner ornaments */}
        {[["top", "left"], ["top", "right"], ["bottom", "left"], ["bottom", "right"]].map(([v, h]) => (
          <div key={v + h} style={{
            position: "absolute", [v]: 16, [h]: 16, width: 32, height: 32,
            borderTop: v === "top" ? "1px solid var(--brass)" : "none",
            borderBottom: v === "bottom" ? "1px solid var(--brass)" : "none",
            borderLeft: h === "left" ? "1px solid var(--brass)" : "none",
            borderRight: h === "right" ? "1px solid var(--brass)" : "none",
          }} />
        ))}

        <svg width="72" height="72" viewBox="0 0 64 64" style={{ margin: "0 auto 24px" }} className="hexmotif">
          <polygon points="32,4 58,17 58,45 32,60 6,45 6,17" fill="none" stroke="var(--brass)" strokeWidth="1.2" />
          <polygon points="32,14 48,21 48,41 32,50 16,41 16,21" fill="none" stroke="var(--brass)" strokeWidth="0.6" opacity="0.7" />
          <text x="32" y="40" textAnchor="middle" fill="var(--brass)" style={{ font: 'italic 24px "Playfair Display", serif' }}>✦</text>
        </svg>

        <div className="overline" style={{ marginBottom: 14 }}>
          ✦ HEXIS OS · Provenance Seal · MMXXVI
        </div>
        <h1 className="font-serif" data-testid="seal-project-name" style={{ fontSize: "clamp(42px, 5vw, 64px)", lineHeight: 1, margin: "0 0 14px", color: "var(--parchment)" }}>
          {seal.name}
        </h1>
        <p className="font-serif" style={{ fontSize: 18, color: "var(--parchment-dim)", fontStyle: "italic", maxWidth: 620, margin: "0 auto 32px", lineHeight: 1.5 }}>
          {seal.description || "— no description inscribed —"}
        </p>

        <div
          data-testid="seal-attest"
          className="font-display"
          style={{ fontSize: 20, color: "var(--brass)", fontStyle: "italic", marginBottom: 18 }}
        >
          This artifact, inscribed by <strong style={{ color: "var(--parchment)" }}>{seal.owner_name}</strong>,
          <br />
          has survived the Six Disciplines and the Crucible of Chaos.
        </div>

        {/* Rank badge */}
        {seal.rank && (
          <div
            data-testid="seal-rank-badge"
            style={{
              display: "inline-block",
              padding: "10px 28px",
              border: `2px solid ${rankColor(seal.rank.name)}`,
              color: rankColor(seal.rank.name),
              marginBottom: 36,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 14,
              letterSpacing: "0.3em",
              fontWeight: 700,
            }}
          >
            ✦  {seal.rank.name.toUpperCase()}  ✦
          </div>
        )}

        {/* Pillars grid */}
        <div
          data-testid="seal-pillars"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 12,
            maxWidth: 540,
            margin: "0 auto 36px",
          }}
        >
          {Object.entries(PILLAR_NAMES).map(([key, [roman, name]]) => {
            const done = seal.pillars_completed.includes(key);
            return (
              <div
                key={key}
                data-testid={`seal-pillar-${key}`}
                style={{
                  padding: "14px 10px",
                  border: `1px solid ${done ? "var(--brass)" : "rgba(197,160,89,0.2)"}`,
                  color: done ? "var(--parchment)" : "var(--muted)",
                  background: done ? "rgba(197,160,89,0.06)" : "transparent",
                }}
              >
                <div className="font-display" style={{ fontSize: 18, color: done ? "var(--brass)" : "var(--muted)", fontStyle: "italic" }}>
                  {roman}.
                </div>
                <div className="font-serif" style={{ fontSize: 16, marginTop: 2 }}>{name}</div>
                <div className="font-mono" style={{ fontSize: 9, letterSpacing: "0.2em", marginTop: 4, color: done ? "var(--success)" : "var(--muted)", textTransform: "uppercase" }}>
                  {done ? "✓ Examined" : "—"}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 40, flexWrap: "wrap", marginBottom: 28 }}>
          <SealStat label="Chaos" value={seal.chaos.passed ? "PASSED" : "—"} color={seal.chaos.passed ? "var(--success)" : "var(--warning)"} />
          <SealStat label="Files" value={String(seal.file_count)} />
          <SealStat label="Model" value={seal.model || "—"} />
          <SealStat label="Probes" value={seal.chaos.real_probes ? "REAL" : "SIM"} color="var(--brass)" />
        </div>

        {(seal.vercel_url || seal.railway_url) && (
          <div className="rule-parchment" style={{ margin: "24px auto", maxWidth: 320 }} />
        )}
        {seal.vercel_url && (
          <div style={{ marginBottom: 8 }}>
            <div className="overline" style={{ marginBottom: 6 }}>Live Deployment</div>
            <a
              data-testid="seal-vercel-url"
              href={seal.vercel_url}
              target="_blank" rel="noreferrer"
              className="font-mono link-brass"
              style={{ fontSize: 13, wordBreak: "break-all" }}
            >
              ▲ {seal.vercel_url}
            </a>
          </div>
        )}
        {seal.railway_url && (
          <div>
            <a
              data-testid="seal-railway-url"
              href={seal.railway_url}
              target="_blank" rel="noreferrer"
              className="font-mono link-brass"
              style={{ fontSize: 13, wordBreak: "break-all" }}
            >
              🚂 {seal.railway_url}
            </a>
          </div>
        )}

        <div style={{ marginTop: 42, paddingTop: 20, borderTop: "1px solid rgba(197,160,89,0.2)", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div className="font-display" style={{ color: "var(--muted)", fontSize: 13, fontStyle: "italic" }}>
            Inscribed {(seal.created_at || "").slice(0, 10)} · Seal ID {seal.project_id.slice(-8)}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              data-testid="seal-download-pdf-btn"
              className="btn-ghost"
              onClick={() => downloadFile("pdf")}
              style={{ padding: "8px 14px" }}
            >
              ⬇ PDF
            </button>
            {seal.file_count > 0 && seal.chaos?.passed && (
              <button
                data-testid="seal-download-zip-btn"
                className="btn-ghost"
                onClick={() => downloadFile("zip")}
                style={{ padding: "8px 14px" }}
              >
                ⬇ ZIP ({seal.file_count})
              </button>
            )}
            <button data-testid="seal-copy-link-btn" className="btn-ghost" onClick={copyLink} style={{ padding: "8px 14px" }}>
              {copied ? "✓ Copied" : "Copy Share Link"}
            </button>
            <button
              data-testid="seal-share-x-btn"
              className="btn-ghost"
              onClick={shareOnX}
              style={{ padding: "8px 14px" }}
              title="Post this seal to X / Twitter"
            >
              𝕏 Share on X
            </button>
          </div>
        </div>
      </div>

      <p
        className="font-display"
        style={{ marginTop: 28, textAlign: "center", color: "var(--muted)", fontSize: 14, fontStyle: "italic" }}
      >
        "We are what we repeatedly do. Excellence, then, is not an act, but a habit." — Ἀριστοτέλης
      </p>
    </div>
  );
}

function SealStat({ label, value, color = "var(--parchment)" }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div className="overline" style={{ fontSize: 10 }}>{label}</div>
      <div className="font-serif" style={{ fontSize: 26, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function rankColor(name) {
  return {
    Master: "#f4b658",
    Journeyman: "#c5a059",
    Apprentice: "#b49b64",
    Novice: "#8c826e",
    Uninitiated: "#6e6a62",
  }[name] || "#c5a059";
}
